/* Bundle ladder ⇄ mix-and-match slots ⇄ gift ladder ⇄ buy form.
 *
 * Picking a tier reveals that many figure slots, unlocks every gift whose
 * threshold the quantity now meets, prices the pack from whatever is actually
 * in the slots, and writes the cart payload into the product form.
 *
 * Pricing. Each slot starts on the product being viewed, so an untouched pack
 * is N of this figure and the Liquid-rendered baseline is exactly right at
 * load — nothing flashes and then corrects itself. Swap a slot for a figure at
 * a different price and the selected tier is recomputed from the real cent
 * prices Liquid put on the options. Unselected tiers keep their baseline: what
 * N of THIS figure would cost. The tier row and the button read from the same
 * computation, so they cannot disagree.
 *
 * Submission. A mixed pack is written as Shopify's own `items[i][id]` inputs
 * and the single `id`/`quantity` inputs are disabled — disabled fields are not
 * submitted, so the cart never receives both shapes at once. One request, one
 * line item per distinct figure, and cart.js needs no special case.
 */
(function () {
  'use strict';

  function TB() {
    return window.TinyBricks || {};
  }

  function money(cents) {
    var tb = TB();
    return typeof tb.formatMoney === 'function' ? tb.formatMoney(cents) : String(cents);
  }

  function selectedTier(root) {
    return root.querySelector('[data-bundle-tier]:checked');
  }

  function tierQty(tier) {
    return tier ? parseInt(tier.dataset.qty, 10) || 1 : 1;
  }

  /* ---------------------------------------------------------- slots ---- */

  /* The visible slots are the first `qty` of them; the rest stay hidden but
     keep their chosen value, so stepping 3 → 2 → 3 does not lose a choice. */
  function showSlots(root, qty) {
    var wrap = root.querySelector('[data-bundle-slots]');
    if (!wrap) return [];
    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-slot]'));
    slots.forEach(function (slot, i) {
      slot.hidden = i >= qty;
    });
    wrap.hidden = qty < 2;
    return slots.slice(0, qty);
  }

  function slotValues(root, qty) {
    return showSlots(root, qty).map(function (slot) {
      var input = slot.querySelector('[data-slot-input]');
      if (!input) return null;
      var price = parseInt(input.dataset.price, 10);
      return { id: input.value, price: isNaN(price) ? 0 : price };
    }).filter(Boolean);
  }

  /* --------------------------------------------------------- picker ---- */

  /* One dialog serves every slot. `openFor` remembers which slot invoked it so
     the choice lands in the right place, and which control to hand focus back
     to on close. */
  var picker = {
    el: null,
    slot: null,
    opener: null
  };

  function openPicker(slot, opener) {
    if (!picker.el) return;
    picker.slot = slot;
    picker.opener = opener;

    // Mark the figure this slot currently holds so the list opens showing the
    // shopper where they are rather than an undifferentiated grid.
    var current = slot.querySelector('[data-slot-input]');
    picker.el.querySelectorAll('[data-picker-option]').forEach(function (option) {
      option.setAttribute(
        'aria-current',
        current && option.dataset.id === current.value ? 'true' : 'false'
      );
    });

    var search = picker.el.querySelector('[data-picker-search]');
    if (search) {
      search.value = '';
      filterPicker('');
    }

    if (typeof picker.el.showModal === 'function') picker.el.showModal();
    else picker.el.setAttribute('open', '');

    // Focusing search on a touch keyboard covers the list, so only pull focus
    // there when there is enough to scroll through to be worth searching.
    var many = picker.el.querySelectorAll('[data-picker-option]').length > 8;
    if (search && many && window.matchMedia('(min-width: 48rem)').matches) search.focus();
  }

  function closePicker() {
    if (!picker.el) return;
    if (typeof picker.el.close === 'function') picker.el.close();
    else picker.el.removeAttribute('open');
    if (picker.opener) picker.opener.focus();
    picker.slot = null;
    picker.opener = null;
  }

  function filterPicker(term) {
    if (!picker.el) return;
    var query = String(term || '').trim().toLowerCase();
    var shown = 0;
    picker.el.querySelectorAll('[data-picker-option]').forEach(function (option) {
      var hit = !query || (option.dataset.search || '').indexOf(query) !== -1;
      var li = option.closest('li');
      if (li) li.hidden = !hit;
      if (hit) shown++;
    });
    var empty = picker.el.querySelector('[data-picker-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  function chooseFigure(root, option) {
    var slot = picker.slot;
    if (!slot) return;

    var input = slot.querySelector('[data-slot-input]');
    var thumb = slot.querySelector('[data-slot-thumb]');
    var name = slot.querySelector('[data-slot-name]');

    if (input) {
      input.value = option.dataset.id;
      input.dataset.price = option.dataset.price;
    }
    if (thumb && option.dataset.thumb) thumb.src = option.dataset.thumb;
    if (name) name.textContent = option.dataset.title;

    closePicker();
    apply(root);
  }

  /* ---------------------------------------------------------- gifts ---- */
  function syncGifts(qty) {
    document.querySelectorAll('[data-gift]').forEach(function (gift) {
      var need = parseInt(gift.dataset.unlockQty, 10) || 1;
      gift.dataset.locked = qty >= need ? 'false' : 'true';
    });
  }

  /* --------------------------------------------------------- pricing --- */

  /* Returns the discounted total in cents for the items currently in the
     slots. Mirrors the Liquid arithmetic exactly: sum, then take the
     percentage off the sum — not per line — so rounding lands the same way. */
  function packTotal(items, discountPct) {
    var gross = items.reduce(function (sum, item) {
      return sum + item.price;
    }, 0);
    var keep = 100 - (discountPct || 0);
    return { gross: gross, net: Math.round((gross * keep) / 100) };
  }

  /* True when every slot still holds the product being viewed. */
  function isUnmixed(root, items) {
    var first = root.querySelector('[data-slot] [data-slot-input]');
    if (!first) return true;
    return items.every(function (item) {
      return item.id === first.value;
    });
  }

  function priceTier(root, tier, items) {
    if (!tier) return;
    var pct = parseInt(tier.dataset.discount, 10) || 0;
    var totals = packTotal(items, pct);

    // Reset every tier to its Liquid baseline, then overwrite only the one
    // that is selected — an unselected tier should still read "N of this
    // figure", which is what the shopper would pay if they switched to it.
    root.querySelectorAll('[data-tier-price]').forEach(function (el) {
      el.textContent = el.dataset.baseline;
    });
    root.querySelectorAll('[data-tier-was]').forEach(function (el) {
      el.textContent = el.dataset.baseline;
    });
    root.querySelectorAll('[data-tier-note]').forEach(function (el) {
      el.textContent = el.dataset.baseline;
    });

    // An unmixed pack is exactly what Liquid already priced, so use its string
    // rather than recomputing it. That makes a rounding disagreement between
    // this script and the `money` filter impossible by construction; the JS
    // arithmetic only ever runs for a genuinely mixed pack, which Liquid never
    // had an opinion about.
    var unmixed = isUnmixed(root, items);
    var row = tier.nextElementSibling;
    var priceEl = row ? row.querySelector('[data-tier-price]') : null;
    var wasEl = row ? row.querySelector('[data-tier-was]') : null;
    var noteEl = row ? row.querySelector('[data-tier-note]') : null;

    if (!unmixed) {
      if (priceEl) priceEl.textContent = money(totals.net);
      if (wasEl && pct > 0) wasEl.textContent = money(totals.gross);
      // A per-figure price is meaningless across figures at different prices,
      // so say what the pack actually saves instead of quoting a unit price
      // that describes none of them.
      if (noteEl) {
        var strings = TB().strings || {};
        var saved = totals.gross - totals.net;
        noteEl.textContent =
          pct > 0 && strings.bundleMixedSaving
            ? strings.bundleMixedSaving.replace('AMOUNT_SLOT', money(saved))
            : strings.bundleMixed || noteEl.dataset.baseline;
      }
    }

    var cta = document.querySelector('[data-cta-price]');
    if (cta) {
      cta.textContent = unmixed
        ? tier.dataset.totalMoney || money(totals.net)
        : money(totals.net);
    }

    return totals;
  }

  /* Collapse duplicates so three of the same figure become one line of
     quantity 3 rather than three lines of one. */
  function collapse(items) {
    var byId = {};
    var order = [];
    items.forEach(function (item) {
      if (!byId[item.id]) {
        byId[item.id] = 0;
        order.push(item.id);
      }
      byId[item.id] += 1;
    });
    return order.map(function (id) {
      return { id: id, quantity: byId[id] };
    });
  }

  /* ------------------------------------------------------ buy form ----- */

  /* Writes the cart payload. A single-figure pack keeps the plain
     `id` + `quantity` path; anything wider is written as `items[i][id]` and
     the single inputs are switched off so only one shape is ever submitted. */
  /* Shopify's dynamic checkout button can only carry one variant, so it is
     swapped for our own whenever the pack is wider than a single item. See the
     comment in product-buy-buttons.liquid — leaving it visible would check the
     shopper out with one figure and charge them for one. */
  function syncExpressCheckout(multi) {
    var native = document.querySelector('[data-dynamic-checkout]');
    var ours = document.querySelector('[data-bundle-buynow]');
    if (native) native.hidden = multi;
    // With GoKwik on, Shopify's button is not rendered at all and ours is the
    // only buy-now there is, so it stays visible at every tier. It must also
    // never start hidden: GoKwik skips invisible buttons when it scans, and
    // its observer watches for added NODES only — revealing this one flips an
    // attribute, which the observer does not see. A button hidden at first
    // paint is a button GoKwik never looks at again.
    if (ours) ours.hidden = native ? !multi : false;
  }

  /* Disabling the field alone leaves a stepper that still looks live: the +/-
     buttons sit outside the input, so they keep their hover state and their
     clicks land. Lock the whole control together and say why on hover, so the
     pack ladder above reads as the thing that owns this number. */
  function setQtyLock(input, locked) {
    var wrap = input.closest('[data-qty]');
    if (!wrap) return;
    wrap.classList.toggle('qty--locked', locked);
    wrap.querySelectorAll('[data-qty-step]').forEach(function (button) {
      button.disabled = locked;
    });
    if (locked) wrap.setAttribute('title', (TB().strings || {}).bundleQtyLocked || '');
    else wrap.removeAttribute('title');
  }

  function syncForm(root, items, qty) {
    var container = document.querySelector('[data-bundle-items]');
    var idInput = document.querySelector('[data-variant-id-input]');
    var qtyInputs = document.querySelectorAll('[data-product-form] [name="quantity"]');

    var mixed = !!container && items.length > 1;
    syncExpressCheckout(items.length > 1);

    if (!mixed) {
      if (container) container.innerHTML = '';
      if (idInput) idInput.disabled = false;
      qtyInputs.forEach(function (input) {
        input.disabled = false;
        setQtyLock(input, false);
        var max = parseInt(input.getAttribute('max'), 10);
        input.value = max && qty > max ? max : qty;
      });
      return;
    }

    container.innerHTML = collapse(items)
      .map(function (line, i) {
        return (
          '<input type="hidden" name="items[' + i + '][id]" value="' + line.id + '">' +
          '<input type="hidden" name="items[' + i + '][quantity]" value="' + line.quantity + '">'
        );
      })
      .join('');

    if (idInput) idInput.disabled = true;
    qtyInputs.forEach(function (input) {
      // Disabled so it is never submitted — items[i][quantity] carries the real
      // numbers — but it is still on screen, so it has to READ right too. Left
      // untouched it kept whatever it last held, which meant a stepper showing
      // 1 sitting directly under a selected 3-pack: two controls stating
      // different quantities for the same order.
      input.value = qty;
      input.disabled = true;
      setQtyLock(input, true);
    });
  }

  /* ----------------------------------------------------------- apply --- */

  /* What the shopper has actually chosen, one entry per figure in the pack. */
  function currentItems(root) {
    /* No bundle block on this product. Buy-now is on every product page now,
       not just the ones with a pack ladder, so this path has to stand on its
       own: the order is whatever the plain stepper says, of whichever variant
       the form currently holds. */
    if (!root) {
      var plainId = document.querySelector('[data-variant-id-input]');
      var plainQty = document.querySelector('[data-product-form] [name="quantity"]');
      var n = plainQty ? parseInt(plainQty.value, 10) || 1 : 1;
      var plain = [];
      for (var j = 0; j < n; j++) plain.push({ id: plainId ? plainId.value : '', price: 0 });
      return plain;
    }

    var qty = tierQty(selectedTier(root));
    var items = slotValues(root, qty);

    // No slots rendered (mix off) — fall back to N of the current figure so
    // pricing and the payload still work.
    if (!items.length) {
      var idInput = document.querySelector('[data-variant-id-input]');
      var unit = parseInt(root.dataset.unitPrice, 10) || 0;
      items = [];
      for (var i = 0; i < qty; i++) items.push({ id: idInput ? idInput.value : '', price: unit });
    }
    return items;
  }

  /* Our stand-in for the dynamic checkout button: build the cart, then hand
     over to whichever checkout the store actually runs. It adds rather than
     replacing, so anything already in the cart survives — a cart permalink
     would have wiped it.

     `gokwikSdk.initCheckout` opens GoKwik on the CART, which is the only entry
     point that can express a pack: GoKwik's own buy-now flow reads a single
     variant off the product form and would check a three-pack out as one
     figure. Falling through to /checkout is the honest fallback for a store
     with GoKwik switched off, or a session where its script never loaded —
     better a native checkout than a button that does nothing. */
  function buyNow(root, button) {
    var tb = TB();
    var payload = collapse(currentItems(root)).filter(function (line) {
      return line.id;
    });
    if (!payload.length) return;

    // Line properties on the form (the pre-order note) go on every line, as
    // they would through add-to-cart — otherwise buy-now drops them.
    var properties = {};
    document.querySelectorAll('[data-product-form] input[name^="properties["]').forEach(function (input) {
      if (!input.disabled) properties[input.name.slice(11, -1)] = input.value;
    });
    if (Object.keys(properties).length) {
      payload.forEach(function (line) {
        line.properties = properties;
      });
    }

    button.dataset.state = 'loading';
    fetch(tb.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        // Respect the merchant's own switch: `goEnable` off means they have
        // deliberately stood GoKwik's checkout down, and we must not reach past
        // that and open it anyway.
        var gk = window.gkBtnConfig || {};
        var gkReady =
          gk.checkoutEnabled !== false &&
          window.gokwikSdk &&
          typeof window.gokwikSdk.initCheckout === 'function';
        if (gkReady) {
          window.gokwikSdk.initCheckout(window.merchantInfo);
          button.dataset.state = '';
          return;
        }
        var root_url = tb.routes && tb.routes.root ? tb.routes.root : '/';
        window.location.href = root_url.replace(/\/+$/, '') + '/checkout';
      })
      .catch(function () {
        button.dataset.state = 'error';
        window.setTimeout(function () {
          button.dataset.state = '';
        }, 2400);
        if (typeof tb.toast === 'function') tb.toast((tb.strings || {}).error);
      });
  }

  /* The dialog sits inside the block a variant change re-renders, so the
     element `picker.el` points at is thrown away and a fresh one takes its
     place — carrying none of these listeners. Left unbound, the chooser opened
     nothing after the first variant swap: the slots were still there, still
     clickable, and silently did nothing until a reload. So this resolves the
     element again and is safe to call repeatedly, on the same pattern as
     bindBuyNow below. */
  function bindPicker(root) {
    picker.el = root.querySelector('[data-figure-picker]');
    if (!picker.el || picker.el.dataset.bound === 'true') return;
    picker.el.dataset.bound = 'true';

    // Clicking the backdrop closes: the dialog element itself is the backdrop,
    // so a click landing on it rather than the panel is outside.
    picker.el.addEventListener('click', function (event) {
      if (event.target === picker.el) closePicker();
    });
    // Escape fires `cancel`/`close` natively; keep our state in step.
    picker.el.addEventListener('close', function () {
      if (picker.opener) picker.opener.focus();
      picker.slot = null;
      picker.opener = null;
    });
    var search = picker.el.querySelector('[data-picker-search]');
    if (search) {
      search.addEventListener('input', function () {
        filterPicker(search.value);
      });
    }
  }

  /* The buy block is replaced wholesale on a variant change, taking this
     button and its listener with it, so binding has to be repeatable. */
  function bindBuyNow(root) {
    var button = document.querySelector('[data-bundle-buynow]');
    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';
    button.addEventListener('click', function () {
      buyNow(root, button);
    });
  }

  function apply(root) {
    var tier = selectedTier(root);
    var qty = tierQty(tier);
    var items = currentItems(root);

    syncGifts(qty);
    priceTier(root, tier, items);
    syncForm(root, items, qty);

    var tb = (window.TinyBricks = window.TinyBricks || {});
    tb.bundleQty = qty;
  }

  function selectTierForQty(root, qty) {
    var matched = false;
    root.querySelectorAll('[data-bundle-tier]').forEach(function (input) {
      var is = (parseInt(input.dataset.qty, 10) || 1) === qty;
      input.checked = is;
      if (is) matched = true;
    });
    return matched;
  }

  function bind() {
    var root = document.querySelector('[data-bundle]');
    if (!root) {
      // No bundle block — the gift ladder can still run off the plain stepper.
      var qtyInput = document.querySelector('[data-product-form] [name="quantity"]');
      if (qtyInput && document.querySelector('[data-gift]')) {
        syncGifts(parseInt(qtyInput.value, 10) || 1);
        qtyInput.addEventListener('change', function () {
          syncGifts(parseInt(qtyInput.value, 10) || 1);
        });
      }
      // The button is rendered on every product page, so it has to be wired on
      // every product page. Left unbound it looked live and did nothing.
      bindBuyNow(null);
      return;
    }

    apply(root);

    bindPicker(root);

    root.addEventListener('change', function (event) {
      if (event.target.matches('[data-bundle-tier]')) apply(root);
    });

    bindBuyNow(root);

    root.addEventListener('click', function (event) {
      var open = event.target.closest('[data-slot-open]');
      if (open) {
        openPicker(open.closest('[data-slot]'), open);
        return;
      }
      if (event.target.closest('[data-picker-close]')) {
        closePicker();
        return;
      }
      var option = event.target.closest('[data-picker-option]');
      if (option) chooseFigure(root, option);
    });

    /* Typing in the field fires `input`; the +/- buttons fire `change`. Only
       `input` was bound, so stepping to 2 moved the number and nothing else —
       no tier selected, no slots revealed, no pricing redone — and the form
       then added 2 units at the undiscounted price while the ladder still
       showed the 1-pack chosen. Bind both. */
    function onQtyEdit(event) {
      if (!event.target.matches('[data-product-form] [name="quantity"]')) return;
      if (event.target.disabled) return;
      if (selectTierForQty(root, parseInt(event.target.value, 10) || 1)) apply(root);
    }
    document.addEventListener('input', onQtyEdit);
    document.addEventListener('change', onQtyEdit);
  }

  /* A variant swap re-renders the tier rows and the buy button from Liquid at
     the new variant's price. Re-apply against the replaced DOM, restoring the
     tier the shopper had chosen. */
  document.addEventListener('tb:section-rendered', function () {
    var root = document.querySelector('[data-bundle]');
    if (!root) return;
    var remembered = TB().bundleQty;
    if (remembered) selectTierForQty(root, remembered);
    bindBuyNow(root);
    bindPicker(root);
    apply(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
